import { usePageModels } from '@/context/PageModels';
import { AppContext } from '@/store';
import { isEmpty } from 'lodash';
import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLog } from '@/features/shared/Hooks/useLog';
import { AISearchMeta } from '@/lib/ai-search/components/AISearchMeta';
import { SEOImage } from '../shared';
import { SEOProps } from './SEO.interface';
import { useSEO } from './useSEO';

const updateLangAttribute = (langCode: string): void => {
  const $html = document.querySelector('html');
  $html?.setAttribute('lang', langCode);
};

const SEO = ({
  metadata,
  tags,
  customTags,
  contentCategory,
  releaseDate,
  publicationDate,
  podcastSeries,
  httpStatusCode = '200',
  isDocumentBackedPage = false,
}: SEOProps) => {
  const Log = useLog({ context: 'SEO' });

  // TEMPORARY LOG FOR TESTING:
  Log('SEO Props from CMS:', {
    metadata,
    tags,
    customTags,
    releaseDate,
    publicationDate,
    podcastSeries,
    contentCategory,
  });

  const pageModels = usePageModels();
  const {
    state: { hrefLang: stateHrefLang },
  } = useContext(AppContext);
  const {
    metaKeywords,
    socialMediaDesc,
    socialMediaImage = {},
    searchDisplayTitle,
    searchDisplayDescription,
    searchDisplayIcon,
    langCode = '',
  } = metadata || {};

  const { language: langCodeDefault = '' } = pageModels;
  React.useEffect(
    () => updateLangAttribute(langCode || langCodeDefault),
    [langCode, langCodeDefault],
  );

  const { robots, customUrl, metaTitle, metaDescription, hasHttpStatusCode } = useSEO({
    metadata,
    httpStatusCode,
    isDocumentBackedPage,
  });

  return (
    <>
      <Helmet>
        {metaTitle && <title>{metaTitle}</title>}
        {metaDescription && <meta name="description" content={metaDescription} />}
        {metaKeywords && <meta name="keywords" content={metaKeywords} />}
        {searchDisplayTitle && <meta property="es:title" content={searchDisplayTitle} />}
        {searchDisplayDescription && (
          <meta property="es:description" content={searchDisplayDescription} />
        )}
        {searchDisplayIcon?.small && (
          <meta property="es:icon" content={searchDisplayIcon.small.url} />
        )}
        {customUrl && <link rel="canonical" href={customUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        {metaTitle && <meta property="og:title" content={metaTitle} />}
        {socialMediaDesc && <meta property="og:description" content={socialMediaDesc} />}
        {customUrl && <meta property="og:url" content={customUrl} />}
        {tags?.length && <meta property="es:tags" content={tags.join(',')} />}
        {robots && <meta name="robots" content={robots.join(',')} />}
        {hasHttpStatusCode && <meta name="prerender-status-code" content={httpStatusCode} />}
        {stateHrefLang?.map(({ hreflang, href }) => (
          <link rel="alternate" hrefLang={hreflang} href={href} key={href} />
        ))}
      </Helmet>
      {!isEmpty(socialMediaImage) && <SEOImage image={socialMediaImage} isFallback={false} />}
      {/* AISearchMeta renders page metadata as meta tags for AI Search crawler */}
      <AISearchMeta
        metadata={metadata}
        contentCategory={contentCategory}
        tags={tags}
        customTags={customTags}
        releaseDate={releaseDate}
        publicationDate={publicationDate}
        podcastSeries={podcastSeries}
      />
    </>
  );
};

export default React.memo(SEO);
